import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './GarminConnect.css';

// API base URL - update for production
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const GarminConnect = ({ trainingPlan, onSyncComplete, onPlanUpdate, autoSync = true }) => {
  const { user, authFetch } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [userId, setUserId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  // Track which plans have been synced to avoid duplicate syncs
  // Initialize from localStorage to persist across page refreshes
  const syncedPlanRef = useRef(localStorage.getItem('garmin_synced_week_id'));

  // Save Garmin workout ID to backend (associates it with the AIthlete workout)
  const saveGarminWorkoutId = useCallback(async (workoutDate, garminWorkoutId, workoutName = null) => {
    if (!authFetch) {
      console.warn('authFetch not available, cannot save Garmin workout ID');
      return false;
    }

    try {
      let url = `/api/user/training-plan/workout/${workoutDate}/garmin-id`;
      if (workoutName) {
        url += `?workout_name=${encodeURIComponent(workoutName)}`;
      }

      const response = await authFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ garmin_workout_id: garminWorkoutId })
      });

      if (response.ok) {
        console.log(`Saved Garmin workout ID ${garminWorkoutId} for workout on ${workoutDate}`);
        return true;
      } else {
        console.warn(`Failed to save Garmin workout ID:`, await response.text());
        return false;
      }
    } catch (err) {
      console.error(`Error saving Garmin workout ID:`, err);
      return false;
    }
  }, [authFetch]);

  // Link Garmin account to internal user
  const linkGarminAccount = useCallback(async (garminUserId) => {
    if (!user?.id) {
      console.warn('No internal user ID available for linking');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/garmin/oauth/link?garmin_user_id=${garminUserId}&internal_user_id=${user.id}`,
        { method: 'POST' }
      );

      if (response.ok) {
        console.log('Garmin account linked to internal user');
      } else {
        console.warn('Failed to link Garmin account');
      }
    } catch (err) {
      console.error('Error linking Garmin account:', err);
    }
  }, [user]);

  // Check for OAuth callback on mount
  useEffect(() => {
    const checkOAuthCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const success = params.get('success');
      const returnedUserId = params.get('user_id');
      const errorParam = params.get('error');

      if (success === 'true' && returnedUserId) {
        // OAuth successful
        setIsConnected(true);
        setUserId(returnedUserId);
        localStorage.setItem('garmin_user_id', returnedUserId);

        // Link Garmin account to internal user for challenge tracking
        await linkGarminAccount(returnedUserId);

        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);

        // Check permissions
        checkConnectionStatus(returnedUserId);

        setSyncStatus({
          type: 'success',
          message: 'Successfully connected to Garmin!'
        });
        setTimeout(() => setSyncStatus(null), 5000);
      } else if (errorParam) {
        setError(`Garmin connection failed: ${errorParam}`);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    checkOAuthCallback();

    // Check if we have a stored user ID
    const storedUserId = localStorage.getItem('garmin_user_id');
    if (storedUserId) {
      checkConnectionStatus(storedUserId);
      // Also ensure linking is done (in case it failed before)
      linkGarminAccount(storedUserId);
    }
  }, [linkGarminAccount]);

  const checkConnectionStatus = async (uid) => {
    try {
      const response = await fetch(`${API_BASE}/api/garmin/oauth/status?user_id=${uid}`);
      const data = await response.json();
      console.log('Garmin status response:', data);

      if (data.connected) {
        setIsConnected(true);
        setUserId(uid);
        const perms = Array.isArray(data.permissions) ? data.permissions : [];
        console.log('Setting permissions:', perms);
        setPermissions(perms);
      } else {
        // Only clear if the backend explicitly says disconnected
        // (not just a network error or transient failure)
        if (data.reason === 'token_expired') {
          console.log('Garmin token expired and refresh failed, clearing stored ID');
          setIsConnected(false);
          localStorage.removeItem('garmin_user_id');
        } else {
          // Backend returned connected: false for other reasons
          setIsConnected(false);
          localStorage.removeItem('garmin_user_id');
        }
      }
    } catch (error) {
      // On network errors, don't clear localStorage - keep the stored ID
      // so we can retry later. Only show as "not connected" in UI.
      console.error('Failed to check Garmin status (network error, keeping stored ID):', error);
      setIsConnected(false);
      // Don't remove from localStorage - might be a transient network issue
    }
  };

  // Check if we have WORKOUT_IMPORT permission
  const hasWorkoutPermission = Array.isArray(permissions) && permissions.includes('WORKOUT_IMPORT');

  // Delete an existing Garmin workout by ID
  const deleteGarminWorkout = useCallback(async (garminWorkoutId) => {
    if (!garminWorkoutId || !userId) return false;

    try {
      console.log(`Deleting existing Garmin workout: ${garminWorkoutId}`);
      const response = await fetch(
        `${API_BASE}/api/garmin/workouts/${garminWorkoutId}?user_id=${userId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        console.log(`Successfully deleted Garmin workout: ${garminWorkoutId}`);
        return true;
      } else {
        console.warn(`Failed to delete Garmin workout ${garminWorkoutId}:`, await response.text());
        return false;
      }
    } catch (err) {
      console.error(`Error deleting Garmin workout ${garminWorkoutId}:`, err);
      return false;
    }
  }, [userId]);

  // Fetch fresh training plan from backend to get latest garmin_workout_ids
  const fetchFreshPlan = useCallback(async () => {
    if (!authFetch) {
      console.warn('authFetch not available, using prop data');
      return null;
    }

    try {
      const response = await authFetch('/api/user/training-plan');
      if (response.ok) {
        const data = await response.json();
        // API returns { success: true, plan: { id, plan_data: { weeks: [...] } } }
        // The trainingPlan prop has weeks at top level, so extract plan_data
        if (data.success && data.plan) {
          const planData = data.plan.plan_data || data.plan;
          console.log('Fetched fresh training plan with garmin_workout_ids:', planData.weeks?.length || 0, 'weeks');
          return planData;
        }
      }
    } catch (err) {
      console.error('Error fetching fresh plan:', err);
    }
    return null;
  }, [authFetch]);

  // Get workouts for the upcoming week (next 7 days)
  const getUpcomingWeekWorkouts = useCallback((plan) => {
    if (!plan?.weeks) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    const upcomingWorkouts = [];

    for (const week of plan.weeks) {
      for (const workout of week.workouts || []) {
        if (!workout.date) continue;

        const workoutDate = new Date(workout.date);
        workoutDate.setHours(0, 0, 0, 0);

        // Include workouts from today through the next 7 days
        if (workoutDate >= today && workoutDate <= nextWeek) {
          upcomingWorkouts.push(workout);
        }
      }
    }

    return upcomingWorkouts;
  }, []);

  // Generate a hash for the current week's workouts to track if synced
  // Includes workout details so changes trigger re-sync
  const getWeekSyncId = useCallback((plan) => {
    if (!plan) return null;
    const workouts = getUpcomingWeekWorkouts(plan);
    if (workouts.length === 0) return null;

    // Create ID based on full workout details so ANY modification triggers re-sync
    return JSON.stringify({
      weekStart: new Date().toISOString().split('T')[0],
      workouts: workouts.map(w => ({
        date: w.date,
        type: w.type,
        name: w.workout_name || w.description || '',
        distance: w.distance_km || 0,
        duration: w.duration_minutes || 0,
        pace_target: w.pace_target || '',
        notes: w.notes || '',
        steps: (w.steps || []).map(s => ({
          step_type: s.step_type || '',
          duration_type: s.duration_type || '',
          duration_value: s.duration_value || 0,
          pace_zone: s.pace_type || s.pace || s.pace_zone || ''
        }))
      }))
    });
  }, [getUpcomingWeekWorkouts]);

  // Auto-sync this week's workouts when conditions are met
  useEffect(() => {
    const weekSyncId = getWeekSyncId(trainingPlan);
    const upcomingWorkouts = getUpcomingWeekWorkouts(trainingPlan);

    // Debug: Log all conditions
    console.log('Auto-sync check:', {
      autoSync,
      isConnected,
      hasWorkoutPermission,
      hasTrainingPlan: !!trainingPlan,
      upcomingWorkoutsCount: upcomingWorkouts.length,
      userId,
      weekSyncId: weekSyncId ? 'exists' : 'null',
      alreadySynced: syncedPlanRef.current === weekSyncId,
      isLoading
    });

    // Auto-sync if:
    // - autoSync is enabled
    // - user is connected with WORKOUT_IMPORT permission
    // - there are upcoming workouts this week
    // - this week hasn't been synced yet
    const shouldAutoSync =
      autoSync &&
      isConnected &&
      hasWorkoutPermission &&
      upcomingWorkouts.length > 0 &&
      userId &&
      weekSyncId &&
      syncedPlanRef.current !== weekSyncId &&
      !isLoading;

    if (!shouldAutoSync) {
      console.log('Auto-sync skipped - conditions not met');
      return;
    }

    console.log(`Auto-syncing ${upcomingWorkouts.length} workouts for this week to Garmin...`);
    syncedPlanRef.current = weekSyncId;
    // Persist to localStorage to survive page refreshes
    localStorage.setItem('garmin_synced_week_id', weekSyncId);

    // Perform the sync
    const performSync = async () => {
      setIsLoading(true);
      setError(null);
      setSyncStatus({
        type: 'info',
        message: `Syncing workouts for this week...`
      });

      try {
        // Fetch fresh plan data to get latest garmin_workout_ids
        const freshPlan = await fetchFreshPlan();
        const planToUse = freshPlan || trainingPlan;
        const workoutsToSync = getUpcomingWeekWorkouts(planToUse);

        // --- Pre-sync cleanup: delete ALL previously synced Garmin workouts ---
        // This handles orphaned workouts (e.g., when a workout was replaced and
        // the new workout doesn't carry the old garmin_workout_id)
        const previouslySyncedIds = JSON.parse(localStorage.getItem('garmin_synced_workout_ids') || '[]');
        // Also collect IDs from current workout objects (in case localStorage is stale)
        const currentWorkoutIds = workoutsToSync
          .map(w => w.garmin_workout_id)
          .filter(Boolean);
        const allIdsToDelete = [...new Set([...previouslySyncedIds, ...currentWorkoutIds])];

        if (allIdsToDelete.length > 0) {
          console.log(`Deleting ${allIdsToDelete.length} previously synced Garmin workouts before re-sync...`);
          for (const oldId of allIdsToDelete) {
            try {
              await deleteGarminWorkout(oldId);
            } catch (e) {
              console.warn(`Failed to delete old Garmin workout ${oldId}:`, e);
            }
          }
          localStorage.removeItem('garmin_synced_workout_ids');
        }

        let syncedCount = 0;
        let failedCount = 0;
        const newSyncedIds = [];

        for (const workout of workoutsToSync) {
          if (workout.type === 'Rest' || !workout.distance_km) continue;

          try {
            // Clear any stale garmin_workout_id on the workout
            const workoutName = workout.workout_name || workout.description || 'Training Workout';
            if (workout.garmin_workout_id) {
              await saveGarminWorkoutId(workout.date, null, workoutName);
            }

            let response;

            if (workout.steps && workout.steps.length > 0) {
              // Use structured workout endpoint - preserves distance/pace from steps
              console.log(`[Garmin Sync] Workout "${workoutName}" has ${workout.steps.length} steps:`, JSON.stringify(workout.steps));
              const structuredSteps = workout.steps.map(s => ({
                step_type: s.step_type || s.type || 'active',
                duration_type: s.duration_type || 'distance',
                // Training plan generator stores distance in meters, time in seconds
                // Garmin expects the same - no conversion needed for time steps
                duration_value: s.duration_value != null
                  ? (s.duration_type === 'distance'
                    ? s.duration_value   // already in meters
                    : s.duration_value)  // already in seconds
                  : null,
                pace_zone: s.pace_type || s.pace || s.pace_zone || null,
                description: s.description || null
              }));
              console.log(`[Garmin Sync] Sending structuredSteps:`, JSON.stringify(structuredSteps));

              const structuredData = {
                workout_name: workoutName,
                steps: structuredSteps,
                description: workout.notes || '',
                schedule_date: workout.date
              };

              response = await authFetch(
                `/api/garmin/workouts/structured?garmin_user_id=${userId}`,
                {
                  method: 'POST',
                  body: JSON.stringify(structuredData)
                }
              );
            } else {
              // Fallback: no structured steps - use easy-run endpoint
              console.log(`[Garmin Sync] Workout "${workoutName}" has NO steps — using easy-run fallback`);
              const workoutData = {
                name: workoutName,
                duration_minutes: workout.duration_minutes || Math.round((workout.distance_km || 5) * 6),
                description: workout.notes || ''
              };

              response = await fetch(
                `${API_BASE}/api/garmin/workouts/easy-run?user_id=${userId}`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(workoutData)
                }
              );
            }

            const usedStructured = workout.steps && workout.steps.length > 0;

            if (response.ok) {
              const data = await response.json();
              console.log('Workout created:', data);
              const newWorkoutId = data.workout?.workoutId;

              // Save the Garmin workout ID to the backend (associates with AIthlete workout)
              if (workout.date && newWorkoutId) {
                await saveGarminWorkoutId(workout.date, String(newWorkoutId), workoutName);
                newSyncedIds.push(String(newWorkoutId));
              }

              // Schedule the workout if not already scheduled by structured endpoint
              if (!usedStructured && workout.date && newWorkoutId) {
                console.log(`Scheduling workout ${newWorkoutId} for ${workout.date}`);
                const scheduleResponse = await fetch(
                  `${API_BASE}/api/garmin/workouts/schedule?user_id=${userId}`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      workout_id: String(newWorkoutId),
                      date: workout.date
                    })
                  }
                );
                const scheduleData = await scheduleResponse.json();
                console.log('Schedule response:', scheduleData);
              } else if (usedStructured) {
                console.log(`Workout ${newWorkoutId} scheduled via structured endpoint for ${workout.date}`);
              } else {
                console.log('Skipping schedule - no date or workoutId:', { date: workout.date, workoutId: newWorkoutId });
              }
              syncedCount++;
            } else {
              console.error('Failed to create workout:', await response.text());
              failedCount++;
            }
          } catch (e) {
            console.error('Error syncing workout:', e);
            failedCount++;
          }
        }

        // Save all new Garmin workout IDs for future orphan cleanup
        localStorage.setItem('garmin_synced_workout_ids', JSON.stringify(newSyncedIds));

        setSyncStatus({
          type: syncedCount > 0 ? 'success' : 'error',
          message: `Synced ${syncedCount} workouts for this week`,
          details: failedCount > 0 ? `${failedCount} workouts failed to sync` : null
        });

        if (onSyncComplete) {
          onSyncComplete({ syncedCount, failedCount });
        }

        // Refresh the training plan to get updated garmin_workout_ids
        if (onPlanUpdate && syncedCount > 0) {
          // The plan will be refreshed by the parent component
        }
      } catch (error) {
        setError(error.message);
        setSyncStatus({
          type: 'error',
          message: 'Failed to sync this week\'s workouts',
          details: error.message
        });
      } finally {
        setIsLoading(false);
      }
    };

    // Small delay to allow UI to settle
    const timer = setTimeout(performSync, 1000);
    return () => clearTimeout(timer);
  }, [trainingPlan, isConnected, hasWorkoutPermission, userId, autoSync, isLoading, getWeekSyncId, getUpcomingWeekWorkouts, onSyncComplete, onPlanUpdate, deleteGarminWorkout, saveGarminWorkoutId, fetchFreshPlan, authFetch]);

  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/garmin/oauth/authorize`);
      const data = await response.json();

      if (data.authorization_url) {
        // Redirect to Garmin OAuth
        window.location.href = data.authorization_url;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      setError(error.message);
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      await fetch(`${API_BASE}/api/garmin/oauth/disconnect?user_id=${userId}`, {
        method: 'DELETE'
      });

      setIsConnected(false);
      setUserId(null);
      setPermissions([]);
      localStorage.removeItem('garmin_user_id');

      setSyncStatus({
        type: 'info',
        message: 'Disconnected from Garmin'
      });
      setTimeout(() => setSyncStatus(null), 3000);
    } catch (error) {
      console.error('Failed to disconnect:', error);
      setError('Failed to disconnect from Garmin');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="garmin-connect">
      {!isConnected ? (
        <div className="garmin-connect-section">
          <button
            className="garmin-connect-btn"
            onClick={handleConnect}
            disabled={isLoading}
          >
            <svg className="garmin-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
            </svg>
            {isLoading ? 'Connecting...' : 'Connect to Garmin'}
          </button>
          <p className="garmin-note">
            Sync workouts automatically to your Garmin watch
          </p>
        </div>
      ) : (
        <div className="garmin-connected">
          <div className="garmin-status-row">
            <div className="garmin-status">
              <svg className="garmin-icon connected" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                <path d="M10 17l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" fill="white"/>
              </svg>
              <span className="connected-text">Connected to Garmin</span>
            </div>
            <button
              className="garmin-disconnect-btn"
              onClick={handleDisconnect}
              disabled={isLoading}
            >
              Disconnect
            </button>
          </div>

          {/* Sync Status Message */}
          {syncStatus && (
            <div className={`garmin-sync-message ${syncStatus.type}`}>
              {syncStatus.type === 'success' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
              )}
              {syncStatus.type === 'info' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                </svg>
              )}
              {syncStatus.type === 'error' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              )}
              <span>{syncStatus.message}</span>
            </div>
          )}

          {/* Default message when no sync status */}
          {!syncStatus && (
            <p className="garmin-sync-note">
              Workouts sync automatically to Garmin Connect
            </p>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="garmin-error">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          {error}
        </div>
      )}
    </div>
  );
};

export default GarminConnect;
